import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
} from 'react-native-reanimated';
import CircleCheckIcon from '@assets/icons/circle-check.svg';
import HammerIcon from '@assets/icons/hammer.svg';
import i18n from '@/utils/i18n';
import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
} from '@/experiences/sales-portal/components/button';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_SPRING_CONFIG_INTRO } from '@experiences/blocs/utils/animations';
import { LayoutChangeEvent, Platform, View } from 'react-native';
import { LayoutSize, useLayoutSizeValue } from '@/contexts/layout-context';
import { OrderEntryMode } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/types';
import { OrderSummaryMetric } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-summary-metric';
import { SegmentedControl } from '@usepepper/ui/molecules/segmented-control';
import {
  selectSalesPortalCartItems,
  useCartStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { shadow } from '@/styles';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useOrderMetrics } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-metrics';
import { useShallow } from 'zustand/react/shallow';

const ANIMATION_DELAY = 400;
const CHECKOUT_BUTTON_WIDTH_COMPACT = 120;
const CHECKOUT_BUTTON_WIDTH_DEFAULT = 200;
const SEGMENTED_CONTROL_WIDTH_COMPACT = 120;
const SEGMENTED_CONTROL_WIDTH_DEFAULT = 200;

interface OrderSummaryBottomBarProps {
  commissionDisplayValue?: string | null;
  currencyCode: string;
  hasItemsInCart?: boolean;
  isCommissionsEnabled?: boolean;
  isCommissionsLoading?: boolean;
  isGrossProfitHidden: boolean;
  onBuild: () => void;
  onCheckout: () => void;
  onLayout?: (event: LayoutChangeEvent) => void;
  onReview: () => void;
  orderEntryMode: OrderEntryMode;
}

export const OrderSummaryBottomBar: React.FC<OrderSummaryBottomBarProps> = ({
  commissionDisplayValue,
  currencyCode,
  isCommissionsLoading,
  isCommissionsEnabled,
  onBuild,
  onCheckout,
  onLayout,
  onReview,
  hasItemsInCart,
  isGrossProfitHidden,
  orderEntryMode,
}) => {
  const [styles, theme] = useStyles();
  const isNative = Platform.OS !== 'web';
  const isCompactLayout = useLayoutSizeValue({
    [LayoutSize.LARGE]: false,
    [LayoutSize.MEDIUM]: false,
    [LayoutSize.SMALL]: true,
    [LayoutSize.SMALL_TABLET]: true,
  });
  const isCompact = isNative && isCompactLayout;
  const checkoutButtonWidth = isCompact
    ? CHECKOUT_BUTTON_WIDTH_COMPACT
    : CHECKOUT_BUTTON_WIDTH_DEFAULT;
  const segmentedControlWidth = isCompact
    ? SEGMENTED_CONTROL_WIDTH_COMPACT
    : SEGMENTED_CONTROL_WIDTH_DEFAULT;

  const cartItems = useCartStore(
    useShallow((state) => selectSalesPortalCartItems(state)),
  );

  const canCheckout = hasItemsInCart;

  const orderMetrics = useOrderMetrics(cartItems, {
    commission: {
      displayValue: commissionDisplayValue,
      isEnabled: isCommissionsEnabled,
      isLoading: isCommissionsLoading,
    },
    currencyCode,
    isGrossProfitHidden,
  });

  const lastMetricIndex = useMemo(() => {
    return orderMetrics.length - 1;
  }, [orderMetrics]);

  // bottom bar animation — animate in on mount
  const translateY = useSharedValue(100);

  useEffect(() => {
    translateY.value = withDelay(
      ANIMATION_DELAY,
      withSpring(0, {
        ...DEFAULT_SPRING_CONFIG_INTRO,
        velocity: 0,
      }),
    );
  }, [translateY]);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: translateY.value }],
    };
  });

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      if (onLayout) {
        onLayout(event);
      }
    },
    [onLayout],
  );

  // segmented control components
  const leftOption = (
    <View style={styles.switchOption}>
      <HammerIcon
        height={16}
        stroke={
          orderEntryMode === OrderEntryMode.Build
            ? theme.sk.color.icon.primary
            : theme.sk.color.icon.secondary
        }
        width={16}
      />
      {!isCompact ? (
        <Typography
          color={
            orderEntryMode === OrderEntryMode.Build
              ? theme.sk.color.text.primary
              : theme.sk.color.text.secondary
          }
          variant="paragraph-md-600"
        >
          {i18n.t('sales_portal.order_entry.control.build')}
        </Typography>
      ) : null}
    </View>
  );
  const rightOption = (
    <View style={styles.switchOption}>
      <CircleCheckIcon
        height={16}
        stroke={
          orderEntryMode === OrderEntryMode.Review
            ? theme.sk.color.icon.primary
            : theme.sk.color.icon.secondary
        }
        width={16}
      />
      {!isCompact ? (
        <Typography
          color={
            orderEntryMode === OrderEntryMode.Review
              ? theme.sk.color.text.primary
              : theme.sk.color.text.secondary
          }
          variant="paragraph-md-600"
        >
          {i18n.t('sales_portal.order_entry.control.review')}
        </Typography>
      ) : null}
      {hasItemsInCart ? (
        <View collapsable={false} style={styles.changesIndicator} />
      ) : null}
    </View>
  );

  return (
    <Animated.View
      onLayout={handleLayout}
      style={[
        styles.container,
        isNative ? styles.containerNative : styles.containerWeb,
        animatedStyle,
      ]}
    >
      <View style={styles.shadowContainer}>
        <View style={styles.content}>
          <View
            style={[
              styles.metricsContainer,
              isCompact ? styles.metricsContainerCompact : undefined,
            ]}
          >
            {orderMetrics.map((metric, index) => (
              <Fragment key={metric.title}>
                <OrderSummaryMetric {...metric} />
                {index < lastMetricIndex ? (
                  <View style={styles.subdivider} />
                ) : null}
              </Fragment>
            ))}
          </View>
          <View style={styles.divider} />
          <View
            style={[
              styles.segmentedControlContainer,
              { width: segmentedControlWidth },
            ]}
          >
            <SegmentedControl
              options={[
                {
                  accessibilityLabel: i18n.t(
                    'sales_portal.order_entry.control.build',
                  ),
                  children: leftOption,
                  key: OrderEntryMode.Build,
                  onPress: onBuild,
                },
                {
                  accessibilityLabel: i18n.t(
                    'sales_portal.order_entry.control.review',
                  ),
                  children: rightOption,
                  key: OrderEntryMode.Review,
                  onPress: onReview,
                },
              ]}
              selectedOption={orderEntryMode}
            />
          </View>
          <View style={styles.divider} />
          <View
            style={[styles.checkoutContainer, { width: checkoutButtonWidth }]}
          >
            {/** TODO (DSR-3479): Disable checkout if a update mutation is in flight  */}
            <Button
              accessibilityLabel={i18n.t('sales_portal.bottom_bar.checkout')}
              disabled={!canCheckout}
              onPress={onCheckout}
              size={ButtonSize.Small}
              variant={ButtonVariant.Primary}
            >
              {i18n.t('sales_portal.bottom_bar.checkout')}
            </Button>
          </View>
        </View>
      </View>
    </Animated.View>
  );
};

const useStyles = createStyles((theme) => ({
  changesIndicator: {
    backgroundColor: theme.sk.color.icon.positive.secondary,
    borderRadius: theme.sk.radii.radiusMedium,
    height: 8,
    width: 8,
  },
  checkoutContainer: {
    alignSelf: 'center',
  },
  container: {
    alignItems: 'center',
    bottom: 0,
    position: 'absolute',
  },
  containerNative: {
    left: 0,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    right: 0,
  },
  containerWeb: {
    alignSelf: 'center',
    maxWidth: '100%',
  },
  content: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusLarge,
    borderWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    zIndex: 1,
  },
  divider: {
    backgroundColor: theme.sk.color.border.primary,
    width: 1,
  },
  metricsContainer: {
    flexDirection: 'row',
    flexShrink: 1,
    gap: theme.sk.spacing.spaceLarge,
    minWidth: 0,
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  metricsContainerCompact: {
    gap: theme.sk.spacing.spaceMedium,
  },
  segmentedControlContainer: {
    alignItems: 'center',
    alignSelf: 'center',
    backgroundColor: theme.sk.color.bg.toggle.off.default,
    borderRadius: theme.sk.radii.radiusRound,
    display: 'flex',
    flexDirection: 'row',
    height: 40,
    padding: theme.sk.spacing.spaceXsmall,
  },
  shadowContainer: {
    borderRadius: theme.sk.radii.radiusLarge,
    marginBottom: theme.sk.spacing.spaceLarge,
    maxWidth: '100%',
    overflow: 'hidden',
    ...shadow({
      opacity: 0.12,
      radius: 24,
      x: 0,
      y: 12,
    }),
  },
  subdivider: {
    alignSelf: 'center',
    backgroundColor: theme.sk.color.border.primary,
    height: 16,
    width: 1,
  },
  switchOption: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    justifyContent: 'center',
  },
}));
